import { useMutation } from "@tanstack/react-query";
import { defineMessages, useIntl } from "react-intl";

import { QueryKeys, queryClient } from "@/api/core";
import { useAdjust } from "@/contexts/adjust-context";
import { useApi } from "@/hooks";
import toast from "@/toast";

import type { Account, Relationship } from "@/schemas";

interface Variables {
  account: Pick<Account, "id" | "username">;
  params?: {
    notify: boolean;
  };
}

const messages = defineMessages({
  followSuccess: {
    id: "accounts.follow.success",
    defaultMessage: "You have successfully followed @{username}.",
  },
  subscribeSuccess: {
    id: "accounts.subscribe.success",
    defaultMessage: "You have successfully subscribed to @{username}.",
  },
  unsubscribeSuccess: {
    id: "accounts.unsubscribe.success",
    defaultMessage: "You have successfully unsubscribed from @{username}.",
  },
  followFailure: {
    id: "accounts.follow.failure",
    defaultMessage: "An error occurred trying to follow this account.",
  },
  subscribeFailure: {
    id: "accounts.subscribe.failure",
    defaultMessage: "An error occurred trying to subscribe to this account.",
  },
  unsubscribeFailure: {
    id: "accounts.unsubscribe.failure",
    defaultMessage: "An error occurred trying to unsubscribe to this account.",
  },
});

function useFollowAccount() {
  const api = useApi();
  const intl = useIntl();
  const { trackEngagement } = useAdjust();

  return useMutation({
    mutationFn: (variables: Variables) =>
      api.post<Relationship>(
        `/api/v1/accounts/${variables.account.id}/follow`,
        {
          ...variables.params,
        },
      ),
    async onMutate(variables) {
      const isFollowing = typeof variables?.params === "undefined";
      const isTogglingSubscription =
        typeof variables.params?.notify === "boolean";
      const isSubscribing =
        isTogglingSubscription && variables.params!.notify === true;
      const isUnsubscribing =
        isTogglingSubscription && variables.params!.notify === false;

      if (isFollowing) {
        trackEngagement("follow");
      }

      const accountQueryKey = QueryKeys.accounts.account(variables.account.id);
      const relationshipQueryKey = QueryKeys.relationships.byAccount(
        variables.account.id,
      );

      // Cancel any outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [accountQueryKey, relationshipQueryKey],
      });

      // Optimistically update the account to the new value.
      const prevAccount = queryClient.getQueryData(accountQueryKey) as Account;
      let nextAccount: Account;
      if (isFollowing && prevAccount) {
        nextAccount = {
          ...prevAccount,
          followers_count: prevAccount.followers_count + 1,
        };
        queryClient.setQueryData(accountQueryKey, nextAccount);
      }

      // Optimistically update the relationship to the new value.
      const prevRelationship = queryClient.getQueryData(
        relationshipQueryKey,
      ) as Relationship;
      let nextRelationship: Relationship;
      if (prevRelationship) {
        nextRelationship = {
          ...prevRelationship,
          following: true,
          notifying: isTogglingSubscription
            ? variables.params!.notify!
            : prevRelationship.notifying,
        };
        queryClient.setQueryData(relationshipQueryKey, nextRelationship);
      }

      return {
        prevAccount,
        prevRelationship,
        isFollowing,
        isSubscribing,
        isUnsubscribing,
      };
    },
    onSuccess(response, variables, context) {
      const queryKey = QueryKeys.relationships.byAccount(variables.account.id);
      queryClient.setQueryData(queryKey, response.data);

      // Render a toast
      if (context) {
        let message = intl.formatMessage(messages.followSuccess, {
          username: variables.account.username,
        });
        if (context.isSubscribing) {
          message = intl.formatMessage(messages.subscribeSuccess, {
            username: variables.account.username,
          });
        } else if (context.isUnsubscribing) {
          message = intl.formatMessage(messages.unsubscribeSuccess, {
            username: variables.account.username,
          });
        }
        toast.success(message);
      }
    },
    onError(_error, variables, context) {
      const accountQueryKey = QueryKeys.accounts.account(variables.account.id);
      const relationshipQueryKey = QueryKeys.relationships.byAccount(
        variables.account.id,
      );

      // Return Relationship back to its original state
      if (context) {
        queryClient.setQueryData(accountQueryKey, context.prevAccount);
        queryClient.setQueryData(
          relationshipQueryKey,
          context.prevRelationship,
        );

        // Render a toast
        let message = intl.formatMessage(messages.followFailure);
        if (context.isSubscribing) {
          message = intl.formatMessage(messages.subscribeFailure);
        } else if (context.isUnsubscribing) {
          message = intl.formatMessage(messages.unsubscribeFailure);
        }
        toast.error(message);
      }
    },
  });
}

export { useFollowAccount };
